import { Box, Button, Card, CardContent, CardProps, makeStyles, useTheme } from '@material-ui/core'
import React from 'react'
import { CardActionProps } from '../cardActions/cardActions'
import { CardBottom } from '../cardBottom/cardBottom'
import { CardTop } from '../cardTop/cardTop'
import { CardWithTransition } from '../cardWithTransition/CardWithTransition'
import { EmptyContent } from '../emptyCardContent/emptyContent'
import { LoadingCard } from '../loadingCard/loadingCard'
import { ButtonOption, SplitButton } from '../splitButton/splitButton'
import { styles } from './standardCard.styles'

interface StandardCardProps extends CardProps {
  title?: string
  titleVariant?: 'primary' | 'secondary'
  subtitle?: string
  noTransition?: boolean
  headerActions?: React.ReactNode
  isLoading?: boolean
  footerActions?: CardActionProps[]
  avatarIcon?: JSX.Element
  showEmptyContent?: boolean
  emptyCaption?: string
  emptyButtonCallback?: () => void // if empty callback is defined then show standardButton when showEmptyContent is active
  emptyButtonCaption?: string
  splitButtonOptions?: ButtonOption[]
  onSplitButtonClick?: (option: ButtonOption) => void
  disabledOptions?: number[]
  splitButtonDisabled?: boolean
  unmountOnExit?: boolean
}

export const StandardCard: React.FC<StandardCardProps> = ({
  className,
  title,
  titleVariant = 'secondary',
  subtitle,
  noTransition = false,
  isLoading = false,
  footerActions,
  avatarIcon,
  headerActions,
  showEmptyContent = false,
  emptyCaption,
  emptyButtonCallback,
  emptyButtonCaption,
  onSplitButtonClick,
  splitButtonOptions,
  children,
  disabledOptions,
  splitButtonDisabled,
  ...cardProps
}) => {
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const shouldShowSplitButton =
    splitButtonOptions && splitButtonOptions.length > 0 && onSplitButtonClick

  const CardContentWrapper: React.FC = () => {
    return (
      <>
        {title && (
          <CardTop
            title={title}
            subtitle={subtitle}
            titleVariant={titleVariant}
            avatarIcon={avatarIcon}
            action={headerActions}
          />
        )}
        {!showEmptyContent ? ( // show cardContent
          <>
            <CardContent className={classes.cardContent}>{!isLoading && children}</CardContent>
            {footerActions && <CardBottom actions={footerActions} />}
          </>
        ) : (
          // if empty callback is defined then show standardButton when showEmptyContent is active
          <Box className={classes.cardEmptyContent}>
            <EmptyContent caption={emptyCaption}>
              {!isLoading && emptyButtonCallback ? (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={emptyButtonCallback}
                >
                  {emptyButtonCaption}
                </Button>
              ) : (
                shouldShowSplitButton && (
                  <SplitButton
                    options={splitButtonOptions}
                    onClick={onSplitButtonClick}
                    disabledOptions={disabledOptions}
                    disabled={splitButtonDisabled}
                  />
                )
              )}
            </EmptyContent>
          </Box>
        )}
      </>
    )
  }
  return (
    <>
      {isLoading ? (
        <LoadingCard className={`${classes.loadingCard}`} />
      ) : noTransition ? (
        <Card className={`${classes.cardWithTransition} ${className || ''}`} raised {...cardProps}>
          <CardContentWrapper />
        </Card>
      ) : (
        <CardWithTransition
          show={true}
          className={`${classes.cardWithTransition} ${className || ''}`}
          raised
          {...cardProps}
        >
          <CardContentWrapper />
        </CardWithTransition>
      )}
    </>
  )
}
